<script>
    import {
        LoadingModal,
        SvelteTable,
        NotificationModal,
        CardLayout,
        CollapsibleTableIcons,
        SendQuoteOrInvoiceToPatientModal,
    } from "../../../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../../../Services/index.svelte";

    import {
        getAmountInCurrency,
        getNumber,
        getText,
        goToURL,
        getPaymentAmountPaid,
        getFormatDate,
        getFormatDateAndTime,
        getDisplayTotal,
        showError,
        showException,
        goToDownloadPage,
        getEstimateStatus,
        showPopupNotification,
        isObjectEmpty,
    } from "../../../../../Utils/utilFunctions.svelte";

    import {
        addIcon,
        InsuranceTypesBypassDisabledComms,
    } from "../../../../../Utils/utilConstants.svelte";

    import {
        getDisplayBillingRuleText,
        getDisplayPaymentDetailsText,
    } from "../../../../PatientEncounter/helpers/encounterHelper.svelte";

    import {
        DirectDoctorStatus,
        DirectEncounterStatus,
        PatientPayInsuranceTypes,
    } from "../../../../../Utils/utilEnums.svelte";
    import SendFeeEstimateDetails from "./SendFeeEstimateDetails.svelte";

    export let patientEncounterDetails = {},
        quotesData = [],
        latestQuoteData = {},
        latestQuoteDataItems = [],
        reloadBillingDetails = () => {},
        reloadBillingDetailsAndUpdateDefaultItems = () => {};

    let isDownloadingQuote = false,
        isRegeneratingQuote = false;

    let isCreateQuoteModalOpen = false,
        creatingQuote = false;

    let selectedEstimateID = Number(0);

    let isCancelQuoteModelOpen = false,
        isCancellingQuote = false;

    let sendQuoteToPatientModalOpen = false,
        selectedQuoteData = {};

    $: hasCurrentQuote =
        quotesData.length && quotesData.some((qut) => qut.status !== 9);

    // No need to check billing rule related properties for Cosmetic encounter with free text code
    $: showCreateQuoteBtn =
        !isObjectEmpty(latestQuoteData) &&
        !hasCurrentQuote &&
        latestQuoteDataItems.length &&
        (latestQuoteData.billingCodesType == 2 ||
            latestQuoteData.billingType != null);

    $: btnOptions = [
        {
            iconName: "createIcon",
            iconFunction: () => _openCreateQuoteModal(),
            btnText: "Create",
            iFont: addIcon,
            hasTooltip: true,
            tooltip: "Create Quote",
            isHidden: !showCreateQuoteBtn,
        },
    ];

    const columns = [
        {
            key: "estimateId",
            title: "ID",
            headerClass: "text-left pl-3 cursor-default w-[5%] rounded-tl-xl",
            class: "text-left px-3 cursor-pointer w-[5%]",
            renderValue: (v) => getNumber(v.id),
        },
        {
            key: "estimateNum",
            title: "Estimate Number",
            headerClass: "text-left pl-3 cursor-default w-[15%]",
            class: "text-left px-3 cursor-pointer w-[15%]",
            renderValue: (v) => getText(v.estimateNumber),
        },
        {
            key: "createDate",
            title: "Create Date",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            renderValue: (v) => _getFormatDate(v.createdDate, v.isExpired),
        },
        {
            key: "expireDate",
            title: "Due Date",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            renderValue: (v) => _getFormatDate(v.expireDate, v.isExpired, true),
        },
        {
            key: "quoteApprove",
            title: "Approved Date",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            renderValue: (v) =>
                _getQuoteApproveDate(v.quoteApprovedDate, v.quoteApproved),
        },
        {
            key: "gapFee",
            title: "Gap Fee",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => getAmountInCurrency(v.actualGap),
        },
        {
            key: "rebate",
            title: "Rebate",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => getAmountInCurrency(v.rebate),
        },
        {
            key: "gst",
            title: "GST",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            value: (v) =>
                getAmountInCurrency(
                    v.addGST ? (v.actualGap + v.rebate) * 0.1 : 0,
                ),
        },
        {
            key: "processingFee",
            title: "Processing Fee",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.internalProcessingFee ?? 0),
        },
        {
            key: "totalFee",
            title: "Total Fee",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => getDisplayTotal(v),
        },
        {
            key: "payments",
            title: "Payments",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => getPaymentAmountPaid(v.payments),
        },
        {
            key: "quoteStatus",
            title: "Status",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            renderValue: (v) => _getStatus(v.status),
        },
        {
            key: "buttons",
            title: "",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl w-[2%]",
            class: "text-left px-3 cursor-pointer",
            renderComponent: {
                component: CollapsibleTableIcons,
                props: {
                    icons: [
                        "downloadIcon",
                        "reGenerateIcon",
                        "sendToPatientIcon",
                        "deleteQuoteIcon",
                    ],
                    tableType: "Fee Estimate",
                    showPatientInDownload: PatientPayInsuranceTypes.includes(
                        patientEncounterDetails?.patient?.insuranceType,
                    ),
                },
            },
        },
    ];

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "buttons") {
            if (classList.contains("downloadIcon")) downloadQuote(rowData.id);
            else if (classList.contains("reGenerateIcon"))
                reGenerateQuote(rowData);
            else if (classList.contains("sendToPatientIcon"))
                _openSendQuoteToPatientModal(rowData);
            else if (classList.contains("deleteQuoteIcon"))
                _openCancelQuoteModel(rowData.id);
        } else if (rowData.id) goToURL(`/Estimates/view/${rowData.id}`);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    const downloadQuote = (id, isRegenerate) => {
        goToDownloadPage(0, id, isRegenerate);
    };

    const reGenerateQuote = async (data) => {
        const { id, doctorId, patientId, patientEncounterId } = data;

        const regenerateObj = {
            type: 0,
            entityId: id,
            doctorId: doctorId,
            patientId: patientId,
            patientEncounterId: patientEncounterId,
        };

        isRegeneratingQuote = true;

        await apiMethods.postMethod
            .regeneratePdfFile(regenerateObj)
            .then((res) => {
                if (res.id) downloadQuote(id, true);
                else
                    showError(
                        "Unable to re-generate this fee estimate PDF, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) =>
                showException(
                    "Unable to re-generate this fee estimate PDF, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isRegeneratingQuote = false;
    };

    const createQuote = async () => {
        creatingQuote = true;

        await apiMethods.postMethod
            .createEstimateFromBillingItem(latestQuoteData.id)
            .then((res) => _handleCreateQuoteFromBillingItem(res))
            .catch((err) => showException(err));

        creatingQuote = false;

        _closeCreateQuoteModal();
    };

    const cancelEstimate = async () => {
        isCancellingQuote = true;

        await apiMethods.putMethod
            .updateEstimateStatus(selectedEstimateID, 9)
            .then((res) => {
                if (res.error) showError(res.error);
                else reloadBillingDetails();
            })
            .catch((err) => showException(err));

        isCancellingQuote = false;

        _closeCancelQuoteModel();
    };

    // HELPER FUNCTIONS
    const _getFormatDate = (date, isExpired, isDateOnly) => {
        if (isExpired) return "Expired";

        if (isDateOnly) return getFormatDate(date);

        return getFormatDateAndTime(date);
    };

    const _getQuoteApproveDate = (date, isApproved) => {
        if (isApproved) return getFormatDate(date);

        return "Not approved";
    };

    const _getStatus = (status) => {
        let statusText, statusColor;

        statusText = getEstimateStatus(status);

        switch (status) {
            case 1:
            case 2:
            case 4:
                statusColor = "#2DBD6F";
                break;
            case 5:
            case 9:
                statusColor = "#E05757";
                break;
            default:
                statusColor = "";
        }

        return `
                <div class="flex justify-start items-center">
                    <div style="color: ${statusColor};">${statusText}</div>
                </div>
                `;
    };

    const _openCreateQuoteModal = () => (isCreateQuoteModalOpen = true);

    const _closeCreateQuoteModal = () => (isCreateQuoteModalOpen = false);

    const _handleCreateQuoteFromBillingItem = async (res) => {
        if (res.error) showError(res.error);
        else if (!res.id)
            showError(
                "Failed to create fee estimate, please refresh the page and try again and contact support if the issue persists.",
            );
        else await reloadBillingDetails();
    };

    const _openCancelQuoteModel = (id) => {
        isCancelQuoteModelOpen = true;
        selectedEstimateID = Number(id);
    };

    const _closeCancelQuoteModel = () => {
        isCancelQuoteModelOpen = false;
        selectedEstimateID = Number(0);
    };

    const _openSendQuoteToPatientModal = (data) => {
        if (
            patientEncounterDetails?.doctor?.status !==
                DirectDoctorStatus.Active &&
            patientEncounterDetails?.doctor?.status !== DirectDoctorStatus.Demo
        )
            return showPopupNotification(
                "Account not active",
                "The fee estimate doctor is not active. Please enable it to send this fee estimate.",
            );
        if (patientEncounterDetails?.status === DirectEncounterStatus.Archived)
            return showPopupNotification(
                "The fee estimate is archived",
                "The fee estimate is archived. Please un-archive it to send this fee estimate.",
            );
        if (
            patientEncounterDetails.disableNotifications &&
            !InsuranceTypesBypassDisabledComms.includes(
                patientEncounterDetails.patient.insuranceType,
            )
        )
            return showPopupNotification(
                "Notifications Disabled",
                "The notifications for this fee estimate are disabled. Please enable notifications to send this fee estimate to the patient.",
            );
        if (patientEncounterDetails?.patient?.deceased)
            return showPopupNotification(
                "Patient Deceased",
                "Unable to send fee estimate, patient deceased.",
            );
        sendQuoteToPatientModalOpen = true;
        selectedQuoteData = {
            ...data,
            type: "quote",
            isFeeEstimate: true,
            patientPhone: patientEncounterDetails.patient.phone,
            patientEmail: patientEncounterDetails.patient.email,
            insuranceType: latestQuoteData.insuranceType,
        };
    };

    const _closeSendQuoteToPatientModal = () => {
        sendQuoteToPatientModalOpen = false;
        selectedQuoteData = {};
    };
</script>

<CardLayout title="Fee Estimates" {btnOptions}>
    {#if quotesData.length}
        <div class="w-full overflow-x-auto">
            <SvelteTable
                {columns}
                rows={quotesData}
                clickCellFunc={({ detail: { event, row, key } }) => {
                    handleClickCell(event, row, key);
                }}
                clickColFunc={({ detail: { event, row, key } }) => {
                    handleClickCol(event, row, key);
                }}
                grayOutFunction={(row) => row.status == 9}
            />
        </div>
    {:else}
        <div class="flex flex-1 flex-col gap-y-2 px-2 py-4 w-full">
            <p>No fee estimates created</p>
            <p>{getDisplayBillingRuleText(latestQuoteData)}</p>
            <p>{getDisplayPaymentDetailsText(latestQuoteData)}</p>
        </div>
    {/if}
</CardLayout>

{#if isDownloadingQuote}
    <LoadingModal titleText="Downloading Fee Estimate PDF..." />
{/if}

{#if isRegeneratingQuote}
    <LoadingModal titleText="Regenerating Fee Estimate PDF..." />
{/if}

<NotificationModal
    title="Confirmation"
    isModalOpen={isCreateQuoteModalOpen}
    submitText="Confirm"
    closeModal={_closeCreateQuoteModal}
    confirmAction={createQuote}
    isConfirming={creatingQuote}
    closeModalFunction={_closeCreateQuoteModal}
>
    <SendFeeEstimateDetails {latestQuoteData}></SendFeeEstimateDetails>
</NotificationModal>

<NotificationModal
    isModalOpen={isCancelQuoteModelOpen}
    cancelText="Delete"
    submitText="Cancel"
    isConfirming={isCancellingQuote}
    isReverseBtns
    confirmAction={_closeCancelQuoteModel}
    closeModal={cancelEstimate}
    closeModalFunction={_closeCancelQuoteModel}
>
    <p class="text-error">Cancel this fee estimate?</p>
</NotificationModal>

<SendQuoteOrInvoiceToPatientModal
    data={selectedQuoteData}
    bind:isModalOpen={sendQuoteToPatientModalOpen}
    afterSendFunc={async () => {
        await reloadBillingDetailsAndUpdateDefaultItems(
            true,
            patientEncounterDetails,
        );

        _closeSendQuoteToPatientModal();
    }}
/>
